import styled from "@emotion/styled"
import { Close, ExpandMoreRounded, KeyboardDoubleArrowDownRounded, SwipeDownRounded } from "@mui/icons-material"
import { Box, Button, CircularProgress, IconButton, Typography } from "@mui/material"
import { grey } from "@mui/material/colors"
import { useCallback, useEffect, useRef, useState } from "react"
import { useHistory } from "react-router-dom"
import { QrReader } from "react-qr-reader"

// ─── Constantes ──────────────────────────────────────────────────────────────

/** Altura del panel inferior en estado colapsado (px) */
const PANEL_COLLAPSED_HEIGHT = 90
/** Altura del panel inferior en estado expandido con ayuda (px) */
const PANEL_EXPANDED_HEIGHT = 420
/** Distancia mínima en px que el usuario debe deslizar hacia abajo para colapsar el panel */
const SWIPE_THRESHOLD = 50
/** Intervalo de polling para detectar el elemento <video> en el DOM (ms) */
const VIDEO_POLL_INTERVAL = 200
/** ID asignado al elemento <video> del QrReader para poder referenciarlo */
const VIDEO_ID = 'qr-reader-video'

// ─── Styled Components ──────────────────────────────────────────────────────

/**
 * Contenedor raíz — ocupa toda la pantalla en posición fija.
 * Utiliza flexbox en columna: QrArea (flex:1) + BottomPanel (altura fija).
 */
const Root = styled('div')(({ theme }) => ({
  height: '100dvh',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#000',
  position: 'fixed',
  top: 0,
  left: 0,
  zIndex: theme.zIndex.modal,
}))

/**
 * Área del lector QR — ocupa todo el espacio restante sobre el BottomPanel.
 * Los overrides CSS internos posicionan absolutamente el <section> que genera
 * react-qr-reader para que el feed de cámara cubra toda el área disponible.
 */
const QrArea = styled('div')({
  flex: 1,
  position: 'relative',
  overflow: 'hidden',
  '& section': {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    '& > div': {
      paddingTop: '0 !important',
      height: '100%',
    },
    '& video': {
      objectFit: 'cover',
      width: '100%',
      height: '100%',
    },
  },
})

/**
 * Panel inferior con bordes redondeados superiores.
 * Se expande/colapsa mediante la prop `expanded` con transición animada.
 * El margen negativo superior (-8px) hace que los bordes redondeados
 * se superpongan visualmente sobre el área de cámara.
 */
const BottomPanel = styled('div', {
  shouldForwardProp: (prop) => prop !== 'expanded',
})(({ theme, expanded }) => ({
  width: '100%',
  minHeight: expanded ? PANEL_EXPANDED_HEIGHT : PANEL_COLLAPSED_HEIGHT,
  maxHeight: '70dvh',
  backgroundColor: '#fff',
  borderTopLeftRadius: 16,
  borderTopRightRadius: 16,
  padding: theme.spacing(3, 2, 2),
  textAlign: 'center',
  flexShrink: 0,
  marginTop: -8,
  paddingBottom: 36,
  position: 'relative',
  zIndex: 1,
  overflow: 'hidden',
  transition: 'height 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
  touchAction: 'none',
  ...theme.applyStyles('dark', {
    backgroundColor: grey[800],
  }),
}))

/** Indicador visual de arrastre en la parte superior del panel */
const Puller = styled('div')(({ theme }) => ({
  width: 36,
  height: 5,
  backgroundColor: grey[300],
  borderRadius: 3,
  margin: '0 auto',
  marginBottom: theme.spacing(1.5),
  ...theme.applyStyles('dark', {
    backgroundColor: grey[600],
  }),
}))

// ─── Componente ──────────────────────────────────────────────────────────────

/**
 * Lector de QR a pantalla completa para dispositivos móviles.
 *
 * Estructura:
 * - Botón de cierre (esquina superior derecha)
 * - Área de cámara con loading mientras inicializa
 * - Panel inferior con leyenda e instrucciones de ayuda expandibles
 *
 * @param {Object} props
 * @param {Function} [props.onClose] - Callback al presionar el botón cerrar. Si no se define, vuelve a la página anterior (history.goBack)
 * @param {Function} props.onResult - Callback cuando el QrReader detecta un resultado
 */
export const LectorQRGenerarOrdenDigitalMobile = ({ title, description, onClose, onResult }) => {
  const history = useHistory()
  const [cameraReady, setCameraReady] = useState(false)
  const [helpExpanded, setHelpExpanded] = useState(false)
  const touchStartY = useRef(null)
  const videoRef = useRef(null)

  // Detecta cuándo el <video> del QrReader comienza a reproducir el feed de cámara.
  // Usa polling porque react-qr-reader no expone un callback para este evento.
  useEffect(() => {
    let timeoutId = null
    let isMounted = true

    const onPlaying = () => {
      if (isMounted) setCameraReady(true)
    }

    const checkVideo = () => {
      const video = document.getElementById(VIDEO_ID)
      if (video) {
        videoRef.current = video
        if (video.readyState >= 2) {
          // El video ya estaba reproduciendo al momento de encontrarlo
          onPlaying()
          return
        }
        video.addEventListener('playing', onPlaying, { once: true })
      } else {
        // El elemento aún no fue montado en el DOM, reintentar
        timeoutId = setTimeout(checkVideo, VIDEO_POLL_INTERVAL)
      }
    }

    checkVideo()

    return () => {
      isMounted = false
      if (timeoutId) clearTimeout(timeoutId)
      if (videoRef.current) {
        videoRef.current.removeEventListener('playing', onPlaying)
      }
    }
  }, [])

  /** Registra la posición Y inicial del toque para detectar dirección del swipe */
  const handleTouchStart = useCallback((e) => {
    touchStartY.current = e.touches[0].clientY
  }, [])

  /** Detecta swipe hacia abajo para colapsar el panel de ayuda */
  const handleTouchEnd = useCallback((e) => {
    if (touchStartY.current === null) return
    const deltaY = e.changedTouches[0].clientY - touchStartY.current
    if (deltaY > SWIPE_THRESHOLD && helpExpanded) {
      setHelpExpanded(false)
    }
    touchStartY.current = null
  }, [helpExpanded])

  return (
    <Root>
      {/* Botón cerrar — ejecuta onClose o vuelve a la página anterior */}
      <IconButton
        aria-label="cerrar lector QR"
        onClick={onClose || (() => history.goBack())}
        sx={{
          position: 'absolute',
          right: 12,
          top: 12,
          color: '#fff',
          backgroundColor: 'rgba(0,0,0,0.4)',
          zIndex: (theme) => theme.zIndex.modal + 1,
          '&:hover': {
            backgroundColor: 'rgba(0,0,0,0.6)',
          },
        }}
      >
        <Close />
      </IconButton>

      {/* Área de cámara con overlay de carga */}
      <QrArea>
        {!cameraReady && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2,
              backgroundColor: '#000',
            }}
          >
            <CircularProgress size={48} sx={{ color: 'primary.main', mb: 2 }} />
            <Typography variant='body2' sx={{ color: 'rgba(255,255,255,0.7)' }}>
              Abriendo cámara...
            </Typography>
          </Box>
        )}
        <QrReader
          constraints={{ facingMode: 'environment' }}
          onResult={onResult}
          videoId={VIDEO_ID}
          style={{ width: '100%' }}
        />
      </QrArea>

      {/* Panel inferior — expandible con instrucciones de ayuda */}
      <BottomPanel
        expanded={helpExpanded}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <Puller />
        {title && (
          <Typography
            variant='subtitle1'
            sx={{
              px: 2,
              mb: 0.5,
              fontWeight: 600,
              fontSize: '0.95rem',
              letterSpacing: '-0.01em',
              color: 'text.primary',
            }}
          >
            {title}
          </Typography>
        )}

        {description && (
          <Typography
            variant='body2'
            sx={{
              px: 2,
              fontWeight: 400,
              lineHeight: 1.6,
              color: 'text.secondary',
              fontSize: '0.84rem',
            }}
            {...{dangerouslySetInnerHTML: { __html: description } }}
          />
        )}

        {!helpExpanded ? (
          <Button
            size='small'
            variant='text'
            color='primary'
            fullWidth
            sx={{
              mt: 2,
              fontWeight: 500,
              fontSize: '0.82rem',
              textTransform: 'none',
              letterSpacing: '0.01em',
            }}
            onClick={() => setHelpExpanded(true)}
            endIcon={<KeyboardDoubleArrowDownRounded />}
          >
            ¿Necesitás ayuda?
          </Button>
        ) : (
          <Box sx={{ mt: 2.5, px: 1.5 }}>
            <Typography
              variant='subtitle2'
              sx={{
                fontWeight: 600,
                fontSize: '0.88rem',
                letterSpacing: '-0.01em',
                color: 'primary.main',
                mb: 1.5,
              }}
            >
              ¿Cómo usar el lector QR?
            </Typography>
            {[
              'Apuntá la cámara hacia el código QR que generó el prestador en la aplicación miconsultorio.',
              'Mantené el dispositivo firme hasta que el código sea leído correctamente.',
              'Una vez leído, seguí las instrucciones en pantalla para completar el proceso.',
            ].map((step, index) => (
              <Typography
                key={index}
                variant='body2'
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.82rem',
                  lineHeight: 1.55,
                  mb: 1,
                  pl: 0.5,
                }}
              >
                {`${index + 1}. ${step}`}
              </Typography>
            ))}
            <Typography
              variant='caption'
              sx={{
                display: 'block',
                textAlign: 'center',
                mt: 2,
                color: 'text.disabled',
                fontSize: '0.75rem',
                fontWeight: 400,
                letterSpacing: '0.02em',
              }}
            >
              Deslizá hacia abajo para cerrar
            </Typography>
            <SwipeDownRounded
              sx={{
                display: 'block',
                mx: 'auto',
                mt: 1,
                fontSize: 22,
                color: 'text.disabled',
              }}
            />
          </Box>
        )}
      </BottomPanel>
    </Root>
  );
}
